import type { OpenerKind, OpenerThemeOverrides } from "@loot-os/api-contract";
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

import { useTRPC } from "./trpc";

type Outputs = TRPCOptionsProxy<AppRouter>["openerStyles"];

/** An opener style as the dashboard API lists it (`toOpenerStyleOption`). */
export type OpenerStyleChoice = inferOutput<Outputs["list"]>["styles"][number];

/** What a style editor saves: the whole style. */
export interface OpenerStyleValues {
  name: string;
  kind: OpenerKind;
  theme: OpenerThemeOverrides;
}

/**
 * The create, update and delete writes of one scope's opener styles (`openerStyles` or
 * `platform.openerStyles`), each refetching that scope's list and toasting on success; a
 * create or update then calls `onSaved`.
 */
export function useOpenerStyleWrites(
  scope: OpenerStyleChoice["scope"],
  { onSaved }: { onSaved?: () => void } = {},
) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const styles = scope === "platform" ? trpc.platform.openerStyles : trpc.openerStyles;
  const done = (message: string, saved: boolean) => {
    void queryClient.invalidateQueries({ queryKey: styles.list.queryKey() });
    toast.success(message);
    if (saved) onSaved?.();
  };
  const create = useMutation(
    styles.create.mutationOptions({ onSuccess: () => done("Opener style created.", true) }),
  );
  const update = useMutation(
    styles.update.mutationOptions({ onSuccess: () => done("Opener style saved.", true) }),
  );
  const remove = useMutation(
    styles.delete.mutationOptions({ onSuccess: () => done("Opener style deleted.", false) }),
  );
  return { create, update, remove };
}
